import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Users, Check, AlertCircle, Upload, X, Clock, Trash2, ChevronDown, TestTube2, Search, Package, ArrowLeft } from 'lucide-react';
import client from '../../api/client';
import { formatDateTime } from '../../utils/date';
import { uploadFile } from '../../utils/upload';
import DateTimePicker from '../../components/DateTimePicker';
import { useApp } from '../../context/AppContext';
import { GARDEN_CENTER } from '../../constants/garden';
import { TAG_CATEGORIES, sortTags, getTagCategory } from '../../constants/tags';
import { toast, confirmDialog } from '../../components/Toast/toast';
import { formatPrice } from '../../utils/price';

export default function BroadcastPage() {
  const [tags, setTags] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);
  const [recipientsCount, setRecipientsCount] = useState(null);
  const [heading, setHeading] = useState('');
  const [text, setText] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const fileRef = useRef(null);
  const { user } = useApp();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Экранирование HTML-символов для безопасной отправки в Telegram
  const escapeHtml = (str) => {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  // История: показываем как обычный текст — теги <b> убираем, экранирование разворачиваем
  const plainText = (html) => String(html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

  // Сборка финального текста: заголовок жирным + обычный текст
  const buildMessage = () => {
    const parts = [];
    if (heading.trim()) parts.push(`<b>${escapeHtml(heading.trim())}</b>`);
    if (text.trim()) parts.push(escapeHtml(text.trim()));
    return parts.join('\n\n');
  };

  const loadTags = useCallback(async () => {
    try {
      const res = await client.get('/admin/tags');
      setTags(sortTags(res.data || []));
    } catch {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await client.get('/admin/broadcast');
      setBroadcasts(res.data || []);
    } catch {}
  }, []);

  // Пока есть рассылка «Отправляется» — подтягиваем историю каждые 10 секунд.
  // Максимум 60 опросов подряд, чтобы не крутиться вечно при упавшей задаче.
  const pollBudgetRef = useRef(60);

  useEffect(() => {
    const hasSending = broadcasts.some(b => b.status === 'sending');
    if (!hasSending || pollBudgetRef.current <= 0) return;
    pollBudgetRef.current -= 1;
    const timer = setTimeout(() => loadHistory(), 10000);
    return () => clearTimeout(timer);
  }, [broadcasts, loadHistory]);

  const loadCount = useCallback(async () => {
    try {
      const tagParam = selectedTags.length > 0 ? `?tag_ids=${selectedTags.join(',')}` : '';
      const res = await client.get(`/admin/broadcast/count${tagParam}`);
      setRecipientsCount(res.data.count);
    } catch {
      setRecipientsCount(null);
    }
  }, [selectedTags]);

  useEffect(() => {
    loadTags();
    loadHistory();
    // Загружаем товары для выбора в рассылке
    client.get('/catalog/products', { params: { limit: 200 } })
      .then(res => setProducts(res.data?.items || []))
      .catch(() => {});
  }, [loadTags, loadHistory]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - imageUrls.length;
    if (remaining <= 0) {
      toast.error('Максимум 10 фото');
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const urls = [];
      for (const file of toUpload) {
        const url = await uploadFile(file);
        urls.push(url);
      }
      setImageUrls(prev => [...prev, ...urls]);
    } catch {
      toast.error('Ошибка загрузки фото');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!text.trim() && !heading.trim()) return;
    setSending(true);
    setResult(null);
    setShowPreviewModal(false);
    try {
      const payload = {
        text: buildMessage(),
        image_urls: imageUrls,
        button_text: buttonText.trim() || null,
        button_url: buttonUrl.trim() || null,
        tag_ids: selectedTags,
      };
      // Если запланированное время уже прошло — отправляем сразу
      const isPast = scheduledAt && new Date(scheduledAt) <= new Date();
      if (scheduledAt && !isPast) {
        payload.scheduled_at = scheduledAt;
      }
      const res = await client.post('/admin/broadcast', payload);
      setResult({
        success: true,
        count: res.data.recipients_count || 0,
        scheduled: !!scheduledAt && !isPast,
        sending: !scheduledAt || isPast || res.data.status === 'sending',
      });
      setHeading('');
      setText('');
      setImageUrls([]);
      setButtonText('');
      setButtonUrl('');
      setScheduledAt('');
      loadHistory();
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Ошибка отправки' });
      loadHistory();
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Удалить рассылку?', { danger: true }))) return;
    try {
      await client.delete(`/admin/broadcast/${id}`);
      loadHistory();
    } catch {
      toast.error('Ошибка удаления');
    }
  };

  const handleTestSend = async () => {
    if (!text.trim() && !heading.trim()) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const payload = {
        text: buildMessage(),
        image_urls: imageUrls,
        button_text: buttonText.trim() || null,
        button_url: buttonUrl.trim() || null,
        tag_ids: [],
      };
      await client.post('/admin/broadcast/test', payload);
      setTestResult({ success: true });
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || 'Ошибка отправки теста' });
    } finally {
      setTestSending(false);
    }
  };

  const formatScheduled = (iso) => {
    if (!iso) return '';
    return formatDateTime(iso);
  };

  const statusLabels = {
    draft: { text: 'Черновик', color: 'bg-[#64748b]/15 text-[#64748b]' },
    scheduled: { text: 'Запланирована', color: 'bg-[#E8A838]/15 text-[#E8A838]' },
    sending: { text: 'Отправляется', color: 'bg-[#6B9B74]/15 text-[#6B9B74]' },
    sent: { text: 'Отправлена', color: 'bg-[#4A6FA5]/15 text-[#4A6FA5]' },
    sent_reported: { text: 'Отправлена', color: 'bg-[#4A6FA5]/15 text-[#4A6FA5]' },
    failed: { text: 'Ошибка', color: 'bg-[#D64545]/15 text-[#D64545]' },
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* 1. Получатели */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#4A6FA5]" />
            <h2 className="text-sm font-semibold text-[#0f172a]">Получатели</h2>
          </div>
          {recipientsCount !== null && (
            <span className="rounded-full bg-[#6B9B74]/10 px-2.5 py-0.5 text-xs font-medium text-[#6B9B74]">
              {recipientsCount} чел.
            </span>
          )}
        </div>
        <div className="space-y-2">
          <button
            onClick={() => setSelectedTags([])}
            className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
              selectedTags.length === 0
                ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                : 'border-[#e2e8f0] bg-white text-[#64748b]'
            }`}
          >
            <Users className="inline h-3 w-3 mr-1" />
            Все
          </button>
          {Object.entries(TAG_CATEGORIES).map(([catKey, cat]) => (
            <div key={catKey} className="space-y-1">
              <span className="text-[10px] text-[#94a3b8]">{cat.label}:</span>
              <div className="flex flex-wrap gap-1.5">
                {tags.filter(t => getTagCategory(t.slug) === catKey).map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={`rounded-xl border px-2.5 py-1 text-[11px] font-medium transition ${
                      selectedTags.includes(tag.id)
                        ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                        : 'border-[#e2e8f0] bg-white text-[#64748b]'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Фото */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0f172a]">Фото (необязательно)</h3>
          <span className="text-[10px] text-[#64748b]">{imageUrls.length}/10</span>
        </div>
        {imageUrls.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {imageUrls.map((url, idx) => (
              <div key={idx} className="relative">
                <img src={url} alt="" className="h-20 w-full rounded-xl object-cover" />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 grid h-5 w-5 place-items-center rounded-full bg-black/50 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {imageUrls.length < 10 && (
          <label className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#e2e8f0] p-4 cursor-pointer transition hover:border-[#374151]/50 hover:bg-[#374151]/5">
            <Upload className="h-6 w-6 text-[#64748b]" />
            <span className="text-xs text-[#64748b]">
              {uploading ? 'Загрузка...' : 'Выберите фото (до 10)'}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {/* 3. Заголовок */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <label className="text-sm font-semibold text-[#0f172a] mb-1 block">Заголовок</label>
        <p className="text-[10px] text-[#64748b] mb-2">Необязательно. Будет показан жирным шрифтом в начале сообщения.</p>
        <input
          type="text"
          value={heading}
          onChange={e => setHeading(e.target.value)}
          placeholder="Например: Новинка сезона!"
          maxLength={120}
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
        />
        <div className="mt-1 text-right text-[10px] text-[#64748b]">{heading.length}/120</div>
      </div>

      {/* 4. Текст сообщения */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <label className="text-sm font-semibold text-[#0f172a] mb-2 block">Текст сообщения</label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Введите текст рассылки..."
          rows={4}
          className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30 resize-none"
        />
        <div className="mt-1 text-right text-[10px] text-[#64748b]">{text.length} символов</div>
      </div>

      {/* Кнопка в сообщении */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <h3 className="text-sm font-semibold text-[#0f172a] mb-1">Кнопка в сообщении</h3>
        <p className="text-[10px] text-[#64748b] mb-3">Необязательно. Привязывает товар — при нажатии откроется карточка товара.</p>

        {selectedProduct ? (
          <div className="flex items-center gap-3 rounded-xl bg-[#f8fafc] p-3 mb-3">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-[#e2e8f0]">
              {selectedProduct.image_url ? (
                <img src={selectedProduct.image_url} alt={selectedProduct.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-lg">🌱</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0f172a] truncate">{selectedProduct.name}</div>
              <div className="text-xs text-[#64748b]">{formatPrice(selectedProduct.price)} ₽</div>
            </div>
            <button
              onClick={() => { setSelectedProduct(null); setButtonText(''); setButtonUrl(''); }}
              className="grid h-8 w-8 place-items-center rounded-lg text-[#64748b] hover:bg-[#D64545]/10 hover:text-[#D64545]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div>
            <button
              onClick={() => setShowProductPicker(!showProductPicker)}
              className="flex w-full items-center gap-2 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#64748b] transition hover:bg-[#f1f5f9]"
            >
              <Package className="h-4 w-4" />
              Выбрать товар из каталога
              <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${showProductPicker ? 'rotate-180' : ''}`} />
            </button>

            {showProductPicker && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-[#e2e8f0] bg-white">
                <div className="sticky top-0 bg-white p-2 border-b border-[#e2e8f0]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#64748b]" />
                    <input
                      type="text"
                      placeholder="Поиск..."
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      className="w-full rounded-lg bg-[#f8fafc] py-1.5 pl-7 pr-2 text-xs outline-none"
                    />
                  </div>
                </div>
                {products
                  .filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()))
                  .slice(0, 50)
                  .map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedProduct(p);
                        setButtonText('Смотреть товар');
                        setButtonUrl(`https://t.me/${GARDEN_CENTER.botUsername}/app?start=product_${p.id}`);
                        setShowProductPicker(false);
                        setProductSearch('');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#f8fafc] transition"
                    >
                      <div className="h-8 w-8 shrink-0 overflow-hidden rounded bg-[#e2e8f0]">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-sm">🌱</div>
                        )}
                      </div>
                      <span className="truncate text-[#0f172a]">{p.name}</span>
                      <span className="ml-auto shrink-0 text-xs text-[#64748b]">{formatPrice(p.price)} ₽</span>
                    </button>
                  ))
                }
              </div>
            )}
          </div>
        )}
      </div>

      {/* Результат */}
      {result && (
        <div className={`flex items-center gap-2 rounded-xl p-3 text-sm ${
          result.success ? 'bg-[#6B9B74]/10 text-[#6B9B74]' : 'bg-[#D64545]/10 text-[#D64545]'
        }`}>
          {result.success ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {result.success
            ? result.scheduled
              ? 'Рассылка запланирована'
              : result.sending
                ? 'Рассылка отправляется...'
                : `Отправлено ${result.count} получателям`
            : result.error}
        </div>
      )}

      {/* Кнопки отправки */}
      <div className="space-y-2">
        <button
          onClick={() => { setScheduledAt(''); setShowPreviewModal(true); }}
          disabled={(!text.trim() && !heading.trim()) || sending}
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#374151] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1f2937] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
          Отправить сейчас
        </button>
        <button
          onClick={() => setShowCalendar(true)}
          disabled={!text.trim() && !heading.trim()}
          className="w-full flex items-center justify-center gap-2 rounded-2xl border border-[#e2e8f0] bg-white py-3 text-sm font-medium text-[#64748b] transition hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Clock className="h-4 w-4" />
          Запланировать
        </button>

        <div className="text-center text-[10px] text-[#64748b] space-y-0.5">
          <p>⚡ Скорость: ~5 сообщений/сек • Батч: 10 шт • Пауза: 2 сек</p>
          <p>⏱ 100 чел ≈ 20 сек • 1000 чел ≈ 3–4 мин</p>
        </div>

        <button
          onClick={handleTestSend}
          disabled={(!text.trim() && !heading.trim()) || testSending}
          className="w-full flex items-center justify-center gap-2 rounded-2xl border border-[#6B9B74] bg-[#6B9B74]/5 py-3 text-sm font-medium text-[#6B9B74] transition hover:bg-[#6B9B74]/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <TestTube2 className="h-4 w-4" />
          {testSending ? 'Отправка...' : 'Отправить тест'}
        </button>
        <p className="text-center text-[10px] text-[#64748b]">
          Отправит сообщение только вам в Telegram для проверки
        </p>

        {testResult && (
          <div className={`flex items-center gap-2 rounded-xl p-2.5 text-xs ${
            testResult.success ? 'bg-[#6B9B74]/10 text-[#6B9B74]' : 'bg-[#D64545]/10 text-[#D64545]'
          }`}>
            {testResult.success ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {testResult.success ? 'Тест отправлен вам в Telegram' : testResult.error}
          </div>
        )}
      </div>

      {/* Выбранная дата */}
      {scheduledAt && (
        <div className="rounded-2xl bg-[#E8A838]/10 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#E8A838]" />
            <span className="text-sm text-[#0f172a]">Запланировано: {formatScheduled(scheduledAt)}</span>
          </div>
          <button onClick={() => setScheduledAt('')} className="text-[#D64545] text-xs">Отменить</button>
        </div>
      )}

      {/* История */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#4A6FA5]" />
            <h3 className="text-sm font-semibold text-[#0f172a]">История рассылок</h3>
          </div>
          {broadcasts.length > 1 && (
            <button
              onClick={() => setHistoryOpen(!historyOpen)}
              className="flex items-center gap-1 text-xs font-medium text-[#374151]"
            >
              {historyOpen ? 'Свернуть' : `Показать всё (${broadcasts.length})`}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
        {broadcasts.length === 0 ? (
          <p className="text-xs text-[#64748b] text-center py-4">Рассылок пока не было</p>
        ) : (
          <div className={`grid grid-cols-1 gap-3 [&>*]:min-w-0 ${(historyOpen ? broadcasts : broadcasts.slice(0, 1)).length > 1 ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
            {(historyOpen ? broadcasts : broadcasts.slice(0, 1)).map(b => {
              const status = statusLabels[b.status] || statusLabels.draft;
              return (
                <div key={b.id} className="rounded-xl bg-[#f8fafc] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#0f172a] truncate">{plainText(b.text)}</p>
                      <p className="text-[10px] text-[#64748b] mt-1">
                        {b.status === 'scheduled'
                          ? `Запланирована: ${formatDateTime(b.scheduled_at)}`
                          : b.sent_at
                            ? `Отправлена: ${formatDateTime(b.sent_at)}`
                            : `Создана: ${formatDateTime(b.created_at)}`}
                      </p>
                      {b.sent_count > 0 && (
                        <p className="text-[10px] text-[#64748b]">
                          Доставлено: {b.sent_count} из {b.recipients_count}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`rounded-xl px-2 py-0.5 text-[10px] font-medium ${status.color}`}>
                        {status.text}
                      </span>
                      {(b.status === 'draft' || b.status === 'scheduled') && (
                        <button
                          onClick={() => handleDelete(b.id)}
                          className="text-[#D64545]/60 hover:text-[#D64545] transition"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Полноэкранный предпросмотр рассылки */}
      {showPreviewModal && (
        <div className="space-y-4">
          <header className="rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                disabled={sending}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#f1f5f9] text-[#64748b] transition active:scale-90 disabled:opacity-50"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">Предпросмотр рассылки</h2>
            </div>
          </header>
          <div className="space-y-4">
            <div className="rounded-xl bg-[#EDEDED] p-3">
              <div className="rounded-xl bg-white p-3 shadow-sm">
                {imageUrls.length > 0 && (
                  <div className={`grid gap-1 mb-2 ${imageUrls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {imageUrls.slice(0, 4).map((url, idx) => (
                      <img key={idx} src={url} alt="" className="rounded-lg max-h-48 w-full object-cover" />
                    ))}
                  </div>
                )}
                <p className="text-sm leading-relaxed text-[#0f172a] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {heading.trim() && <><b>{heading.trim()}</b>{text.trim() && <><br /><br /></>}</>}
                  {text.trim()}
                </p>
                {buttonText && (
                  <div className="mt-2 rounded-lg bg-[#374151] px-3 py-1.5 text-center text-xs text-white">
                    {buttonText}
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-[10px] text-[#64748b] text-center">Так сообщение выглядит у получателя</p>
            </div>

            <div className="rounded-xl bg-[#f8fafc] p-3">
              <p className="text-xs text-[#64748b]">
                Получателей: <span className="font-medium text-[#0f172a]">{recipientsCount || 0} чел.</span>
                {scheduledAt && (
                  <><br />Запланировано: <span className="font-medium text-[#0f172a]">{formatScheduled(scheduledAt)}</span></>
                )}
              </p>
            </div>
          </div>
          <footer className="shrink-0 border-t border-[#e2e8f0] bg-white/95 px-4 py-3 backdrop-blur-xl">
            {/* Единый порядок: Отмена слева, действие справа (как в редакторах) */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreviewModal(false)}
                disabled={sending}
                className="flex-1 rounded-xl border border-[#e2e8f0] bg-white py-2.5 text-sm font-medium text-[#64748b] transition hover:bg-[#f8fafc] disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-[#374151] py-2.5 text-sm font-semibold text-white transition hover:bg-[#1f2937] disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {sending ? 'Отправка...' : 'Подтвердить'}
              </button>
            </div>
          </footer>
        </div>
      )}

      {/* Календарь */}
      {showCalendar && (
        <DateTimePicker
          value={scheduledAt}
          onChange={(val) => {
            setScheduledAt(val);
            if (val) setShowPreviewModal(true);
          }}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
}
